import type {
  DeactivateContractItemMutationOptions,
  DeactivateContractItemMutationVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysContract } from '../queryKeys';

import { deactivateContractItem } from './deactivateContractItem';

/**
 * Deactivates a persisted contract item and refreshes its contract views.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/deactivate_contract_item_contracts__contract_id__items__item_id__deactivate_post
 */
export function useMutationDeactivateContractItem(
  options?: DeactivateContractItemMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      itemId,
    }: DeactivateContractItemMutationVariables) =>
      deactivateContractItem(contractId, itemId),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.get(variables.contractId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItems(variables.contractId, undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItem(
            variables.contractId,
            variables.itemId,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getHistory(variables.contractId),
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
  });
}
